<template>
	<div class="text-widget-field">
		<default-widget-field v-bind="imageEditorFieldProps" v-on="defaultFieldListeners" />
		<div :ref="fieldRef" style="height: 600px">
			<ImageEditor
				ref="ImageEditor"
				v-bind="editorDefaults"
				:src="imageSrc"
				:imageCropAspectRatio="imageCropAspectRatio"
				:cropSelectPresetOptions="cropSelectPresetOptions"
				:imageScrambler="imageScrambler"
			/>
		</div>
	</div>
</template>

<script>
import DefaultWidgetField from './DefaultWidgetField';
import { uploadWidgetFile } from '@/utils/helpers';
import WidgetFieldMixin from './WidgetFieldMixin';
import {
	createDefaultImageReader,
	setPlugins,
	plugin_crop,
	plugin_finetune,
	plugin_annotate,
	locale_en_gb,
	plugin_crop_locale_en_gb,
	plugin_finetune_locale_en_gb,
	plugin_annotate_locale_en_gb,
	markup_editor_locale_en_gb,
	createDefaultShapePreprocessor,
	markup_editor_defaults,
	plugin_finetune_defaults,
	createDefaultImageScrambler,
	plugin_redact,
	plugin_redact_locale_en_gb,
	createDefaultImageWriter,
} from '@pqina/pintura';
setPlugins(plugin_crop, plugin_finetune, plugin_annotate, plugin_redact);
import { PinturaEditor as ImageEditor } from '@pqina/vue-pintura/vue-2';
import { mapGetters } from 'vuex';
import WidgetFormService from '@/components/WidgetForm/WidgetFormService';

export default {
	name: 'ImageEditorInputField',
	methods: {
		async handleEditorProcess() {
			await this.$refs.ImageEditor.editor
				.processImage()
				.then((imageWriterOutput) => imageWriterOutput)
				.then(({ dest: file }) => uploadWidgetFile({ file, widgetId: this.widgetId, onUploadProgress: null }))
				.then((link) => {
					this.value = link;
				});
		},
	},
	components: {
		ImageEditor,
		DefaultWidgetField,
	},
	props: {
		previousSteps: { type: Object },
		replaceInsertDataStringDict: { type: Object },
		configurationsMappingBlocksUUIDs: { type: Array },
	},
	mixins: [WidgetFieldMixin],
	computed: {
		...mapGetters('selectedNode', ['flatPreviousSteps']),
		widgetId() {
			return this.field.widget;
		},
		currentPreviousSteps() {
			return this.previousSteps || this.flatPreviousSteps;
		},
		imageEditorFieldProps() {
			return { ...this.defaultFieldProps, titleEnabled: false };
		},
		metadataOptions() {
			return this.field.metadata.options;
		},
		imageSrc() {
			// returning this.fieldValue ensures that the picture won't get set to the URL from the settings once user clicks done
			const imageSrc = WidgetFormService.processPreviousSteps(this.metadataOptions.image_src.value, this.currentPreviousSteps);
			return this.fieldValue || imageSrc;
		},
		mimeType() {
			return this.metadataOptions.file_format.value;
		},
		imageWriterDefaults() {
			return this.mimeType ? { mimeType: this.mimeType } : {};
		},
		editorDefaults() {
			return {
				// This will read the image data (required)
				imageReader: createDefaultImageReader(),
				imageWriter: createDefaultImageWriter(this.imageWriterDefaults),
				...markup_editor_defaults,
				...plugin_finetune_defaults,
				// This handles complex shapes like arrows / frames
				shapePreprocessor: createDefaultShapePreprocessor(),
				// This will set a square crop aspect ratio
				imageCropAspectRatio: 1,
				enableButtonExport: false,
				// The icons and labels to use in the user interface (required)
				locale: {
					...locale_en_gb,
					...plugin_crop_locale_en_gb,
					...plugin_finetune_locale_en_gb,
					...plugin_annotate_locale_en_gb,
					...markup_editor_locale_en_gb,
					...plugin_redact_locale_en_gb,
				},
			};
		},
	},
	data: () => ({
		imageScrambler: createDefaultImageScrambler({ scrambleAmount: 2 }),
		cropSelectPresetOptions: [
			[
				'Crop',
				[
					[undefined, 'Custom'],
					[1, 'Square'],
					[4 / 3, 'Landscape'],
					[3 / 4, 'Portrait'],
				],
			],
		],

		imageCropAspectRatio: 0,
	}),
};
</script>

<style scoped lang="scss">
@import '../../../../node_modules/@pqina/pintura/pintura.css';
.selected-input {
	::v-deep {
		&:focus {
			background-color: #f5f8fe;
		}
	}
}
.text-widget-field {
	::v-deep {
		input:-webkit-autofill {
			// remove chrome blue background on autocomplete input
			&,
			&:hover,
			&:focus,
			&:active {
				-webkit-box-shadow: 0 0 0 30px white inset !important;
			}
		}
	}
}
</style>
